/* eslint-disable jsx-a11y/anchor-is-valid */
import {Visibility, VisibilityOff} from '@mui/icons-material'
import clsx from 'clsx'
import {useFormik} from 'formik'
import {useEffect, useRef, useState} from 'react'
import {useDispatch} from 'react-redux'
import {Link, useNavigate} from 'react-router-dom'
import 'react-toastify/dist/ReactToastify.css'
import {cooperAction, officeAction, userAction} from 'redux/action'
import {loginAlert, loginFailAlert, resultAlert} from 'utils/CustomAlert/Alert'
import {loginCookieAction, loginHashAction} from 'utils/func/justUtils'
import * as Yup from 'yup'
import {getUserByToken} from '../core/_requests'
import {useAuth} from '../core/Auth'

const loginSchema = Yup.object().shape({
  password: Yup.string()
    .min(2, '올바른 형식의 비밀번호가 아닙니다.')
    .max(20, '올바른 형식의 비밀번호가 아닙니다.')
    .required('비밀번호를 입력해주세요.'),
  id: Yup.string()
    .min(2, '아이디는 2글자 이상입니다.')
    .max(20, '아이디는 20글자 이하입니다.')
    .required('아이디를 입력해주세요.'),
})

export function Login() {
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const {saveAuth, setCurrentUser} = useAuth()
  const inputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const initialValues = {id: '', password: ''}

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      try {
        await loginHashAction(values).then(async (res) => {
          if (res) {
            await loginAlert(res.name)
            saveAuth(res)
            await getUserByToken(res?.token)
            await loginCookieAction(res?.token)
            setCurrentUser(res)
            dispatch(await userAction('request', res))
            dispatch(await officeAction('request', res))
            dispatch(await cooperAction('request', res))
            if (res.accountType === 'customer_client') {
              navigate('/customer/client', {replace: true})
            } else if (res.accountType === 'installer') {
              navigate('/install/connect', {replace: true})
            } else {
              navigate('/home', {replace: true})
            }
          } else {
            saveAuth(undefined)
            setStatus('The login detail is incorrect')
            setSubmitting(false)
            setLoading(false)
            await loginFailAlert()
          }
        })
      } catch (e: any) {
        console.log(e)
        saveAuth(undefined)
        setStatus('The login detail is incorrect')
        setSubmitting(false)
        setLoading(false)
        await resultAlert({resultCode: e.response.status})
      }
    },
  })

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  return (
    <div className={'h-full grid'}>
      <form
        className={'w-full h-full grid items-center'}
        onSubmit={formik.handleSubmit}
        noValidate
        id='kt_login_signin_form'
      >
        {/* 아이디 */}
        <div className={'mb-10'}>
          <label className={'form-label fs-4 fw-bold text-white'}>아이디</label>
          <input
            style={{imeMode: 'disabled'}}
            placeholder={'아이디를 입력해주세요.'}
            {...formik.getFieldProps('id')}
            className={clsx(
              'form-control form-control-lg form-control-solid',
              {'is-invalid': formik.touched.id && formik.errors.id},
              {'is-valid': formik.touched.id && !formik.errors.id}
            )}
            type='text'
            name='id'
            autoComplete='off'
            ref={inputRef}
          />
          {formik.touched.id && formik.errors.id && (
            <div className={'fv-plugins-message-container'}>
              <span className={'text-danger fw-bolder'} role='alert'>
                {formik.errors.id}
              </span>
            </div>
          )}
        </div>

        {/* 비밀번호 */}
        <div className={'mb-10'}>
          <div className={'flex justify-between items-center mb-2'}>
            <span className={'form-label fw-bolder text-white fs-4 mb-0'}>비밀번호</span>
            <span
              className={'fs-5 text-white fw-bold cursor-pointer hover:underline'}
              onClick={() => navigate('/password/certification')}
            >
              비밀번호 재설정
            </span>
          </div>
          <div className={'relative'}>
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder={'비밀번호를 입력해주세요.'}
              autoComplete='off'
              {...formik.getFieldProps('password')}
              className={clsx(
                'form-control form-control-lg form-control-solid pr-12',
                {'is-invalid': formik.touched.password && formik.errors.password},
                {'is-valid': formik.touched.password && !formik.errors.password}
              )}
            />
            <button
              type='button'
              onClick={() => setShowPassword(!showPassword)}
              className={
                'absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700'
              }
            >
              {showPassword ? <VisibilityOff fontSize='small' /> : <Visibility fontSize='small' />}
            </button>
          </div>
          {formik.touched.password && formik.errors.password && (
            <div className={'fv-plugins-message-container'}>
              <span className={'text-danger fw-bolder'} role='alert'>
                {formik.errors.password}
              </span>
            </div>
          )}
        </div>

        {/* 로그인 버튼 */}
        <div className={'text-center'}>
          <button
            type='submit'
            id='kt_sign_in_submit'
            className={'btn btn-lg btn-dark w-100 mb-5'}
            disabled={formik.isSubmitting || !formik.isValid}
          >
            {!loading ? (
              <span className={'indicator-label fw-bolder'}>로그인</span>
            ) : (
              <span className={'indicator-progress'} style={{display: 'block'}}>
                로그인 중입니다. 잠시만 기다려주세요...
                <span className={'spinner-border spinner-border-sm align-middle ms-2'} />
              </span>
            )}
          </button>
        </div>

        {/* 회원가입 버튼 */}
        <div className={'self-center'}>
          <Link to={'/join/agree'}>
            <div className={'text-center'}>
              <button type='button' className={'btn btn-lg bg-white w-100'}>
                <span className={'fw-bolder text-dark'}>회원가입</span>
              </button>
            </div>
          </Link>
        </div>
      </form>
    </div>
  )
}
